import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { formatMongoDate, parseMongoDate } from '../utils/dateUtils.js';
import "../styles/theme.css";
import "../styles/ViewAdminSchedules.css";
import "../styles/ViewPreceptorSchedule.css";
import "../styles/ViewSchedulesModern.css";
import "../styles/PerceptorAvailabilityForm.css";
import ExportButton from './ExportButton';
import { exportData, exportRenderedTableData, exportToPDF } from '../utils/exportUtils';
import LoadingSpinner from './LoadingSpinner';
import { useToast } from '../utils/toastContext';

// Reusable Off component for empty slots
const OffBadge = () => (
    <span style={{
        display: 'inline-block',
        padding: '6px 14px',
        background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
        color: '#475569',
        borderRadius: '8px',
        fontSize: '0.875rem',
        fontWeight: '600',
        border: '1px solid #cbd5e1',
        textAlign: 'center',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        letterSpacing: '0.5px',
        textTransform: 'uppercase'
    }}>
        🚫 Off
    </span>
);

const getRotationStudents = (rotation) => {
    if (Array.isArray(rotation?.students_data) && rotation.students_data.length > 0) {
        return rotation.students_data;
    }
    if (Array.isArray(rotation?.students)) {
        return rotation.students;
    }
    return [];
};

// Modern Column Selector Component
const ColumnSelector = ({ availableColumns, visibleColumns, onColumnToggle, onExportTable }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown on outside click
    useEffect(() => {
        if (!isOpen) return;
        const handleClick = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [isOpen]);

    const getColumnLabel = (column) => {
        const labels = {
            'rotation': 'Rotation',
            'dateRange': 'Date Range', 
            'students': 'Students',
            'name': 'Student Name',
            'email': 'Email',
            'role': 'My Role'
        };
        return labels[column] || column.charAt(0).toUpperCase() + column.slice(1);
    };

    // Export button style matching AdminResourceCenter "Export Resources" button exactly
    const exportButtonStyle = {
        background: 'linear-gradient(135deg, #f7fbfc 0%, #edf6f8 100%)',
        color: '#134b5f',
        border: '1.5px solid #7aa7b6',
        borderRadius: 14,
        padding: '14px 32px',
        fontWeight: 600,
        fontSize: 16,
        letterSpacing: 0.5,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        outline: 'none',
        boxShadow: '0 10px 24px rgba(19, 75, 95, 0.12)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    };

    return (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <div className="column-selector-container" style={{ position: 'relative' }} ref={dropdownRef}>
                <button 
                    className="column-selector-button"
                    onClick={() => setIsOpen(!isOpen)}
                    style={{
                        background: 'linear-gradient(135deg, #f7fbfc 0%, #edf6f8 100%)',
                        color: '#134b5f',
                        border: '1.5px solid #7aa7b6',
                        borderRadius: 14,
                        padding: '14px 32px',
                        fontWeight: 600,
                        fontSize: 16,
                        letterSpacing: 0.5,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        outline: 'none',
                        boxShadow: '0 10px 24px rgba(19, 75, 95, 0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.background = 'linear-gradient(135deg, #e4f4f7 0%, #ffffff 100%)';
                        e.target.style.boxShadow = '0 14px 28px rgba(19, 75, 95, 0.16)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.background = 'linear-gradient(135deg, #f7fbfc 0%, #edf6f8 100%)';
                        e.target.style.boxShadow = '0 10px 24px rgba(19, 75, 95, 0.12)';
                    }}
                >
                    <span style={{ fontSize: '1rem' }}>📊</span>
                    <span>Customize Columns</span>
                    <span style={{ 
                        fontSize: '0.75rem', 
                        transition: 'transform 0.2s',
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}>▼</span>
                </button>
                {isOpen && (
                    <div style={{
                        position: 'absolute',
                        top: '100%',
                        right: '0',
                        background: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '16px',
                        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                        zIndex: 1000,
                        minWidth: '320px',
                        maxHeight: '400px',
                        overflowY: 'auto',
                        marginTop: '0.5rem',
                        animation: 'modalFadeIn 0.2s ease-out'
                    }}>
                        <div style={{
                            background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
                            padding: '1.25rem',
                            borderRadius: '16px 16px 0 0',
                            color: '#fff'
                        }}>
                            <h4 style={{
                                margin: 0,
                                fontSize: '1.1rem',
                                fontWeight: '700',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                color: '#ffffff'
                            }}>
                                <span>🎯</span>
                                Select Table Columns
                            </h4>
                            <p style={{
                                margin: '0.5rem 0 0 0',
                                fontSize: '0.85rem',
                                opacity: 0.9
                            }}>
                                Choose which columns to display in your table
                            </p>
                        </div>
                        <div style={{ padding: '1.5rem' }}>
                            {availableColumns.map(column => (
                                <label key={column} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    padding: '0.75rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    borderRadius: '10px',
                                    marginBottom: '0.5rem',
                                    background: visibleColumns.includes(column) ? '#f0f9ff' : 'transparent',
                                    border: `2px solid ${visibleColumns.includes(column) ? '#3b82f6' : 'transparent'}`
                                }}
                                onMouseEnter={(e) => {
                                    if (!visibleColumns.includes(column)) {
                                        e.target.style.background = '#f8fafc';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!visibleColumns.includes(column)) {
                                        e.target.style.background = 'transparent';
                                    }
                                }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={visibleColumns.includes(column)}
                                        onChange={() => onColumnToggle(column)}
                                        style={{
                                            width: '18px',
                                            height: '18px',
                                            cursor: 'pointer',
                                            accentColor: '#3b82f6'
                                        }}
                                    />
                                    <span style={{
                                        color: '#374151',
                                        fontSize: '0.95rem',
                                        fontWeight: visibleColumns.includes(column) ? '600' : '500'
                                    }}>
                                        {getColumnLabel(column)}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- SORTABLE TABLE HEADER COMPONENT ---
const SortableTableHeader = ({ label, columnKey, sortState, onSort, style = {} }) => {
    const isActive = sortState.column === columnKey;
    const direction = isActive ? sortState.direction : undefined;
    return (
        <th
            onClick={() => onSort(columnKey)}
            style={{
                ...style,
                cursor: 'pointer',
                userSelect: 'none',
                position: 'relative',
                transition: 'all 0.2s',
                color: '#fff',
                fontWeight: isActive ? '700' : '600',
                textShadow: isActive ? '0 2px 4px rgba(0, 0, 0, 0.5)' : '0 2px 4px rgba(0, 0, 0, 0.3)',
                padding: '14px 32px',
                boxShadow: '0 10px 24px rgba(19, 75, 95, 0.12)'
            }}
        >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {label}
                <span style={{ fontSize: '0.8rem', marginLeft: 4, display: 'inline-block', width: 16 }}>
                    {isActive ? (
                        direction === 'asc' ? '▲' : '▼'
                    ) : (
                        <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>▲▼</span>
                    )}
                </span>
            </span>
        </th>
    );
};

const ViewPreceptorSchedule = () => {
    const { showError } = useToast();
    const [originalScheduleData, setOriginalScheduleData] = useState({}); // Store original data
    const [currentCurriculums, setCurrentCurriculums] = useState([]); // Store current curriculums
    const [selectedCurriculum, setSelectedCurriculum] = useState('');
    const [schedulesByCurriculum, setSchedulesByCurriculum] = useState({});
    const [availableCurriculums, setAvailableCurriculums] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [configurations, setConfigurations] = useState({});
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    useEffect(() => {
        if (startDate && endDate && endDate < startDate) {
            setEndDate('');
        }
    }, [startDate, endDate]);

    useEffect(() => {
        if (!error) return;
        showError(error);
        setError('');
    }, [error, showError]);
    
    // Column management state - now per table
    const [availableColumns] = useState(['rotation', 'dateRange', 'students']);
    const [visibleColumnsByTable, setVisibleColumnsByTable] = useState({});

    // --- SORTING STATE ---
    // Add sorting state for each curriculum table
    const [sortState, setSortState] = useState({});

    // Sorting handler
    const handleSort = (curriculumId, column) => {
        setSortState(prev => {
            const prevState = prev[curriculumId] || { column: '', direction: 'asc' };
            let direction = 'asc';
            if (prevState.column === column) {
                direction = prevState.direction === 'asc' ? 'desc' : 'asc';
            }
            return { ...prev, [curriculumId]: { column, direction } };
        });
    };

    // --- SORTING LOGIC ---
    function getSortedData(data, curriculumId) {
        const sortConfig = sortState[curriculumId];
        if (!sortConfig || !sortConfig.column) return data;
        
        const { column, direction } = sortConfig;
        
        const sorted = [...data].sort((a, b) => {
            let aValue, bValue;
            
            if (column === 'rotation') {
                // Use actual rotation number, ensure proper integer comparison
                aValue = a.rotation !== undefined ? parseInt(a.rotation) : data.indexOf(a);
                bValue = b.rotation !== undefined ? parseInt(b.rotation) : data.indexOf(b);
            } else if (column === 'dateRange') {
                // Sort by start date if available
                aValue = parseMongoDate(a.start_date) || new Date(0);
                bValue = parseMongoDate(b.start_date) || new Date(0);
            } else if (column === 'students') {
                // Sort by number of students
                aValue = Array.isArray(a.students) ? a.students.length : 0;
                bValue = Array.isArray(b.students) ? b.students.length : 0;
            } else {
                // Default fallback
                aValue = a[column] || '';
                bValue = b[column] || '';
            }
            
            // Handle null/undefined values
            if (aValue === undefined || aValue === null) aValue = '';
            if (bValue === undefined || bValue === null) bValue = '';
            
            // Handle date comparisons
            if (column === 'dateRange' && aValue instanceof Date && bValue instanceof Date) {
                return direction === 'asc' ? aValue.getTime() - bValue.getTime() : bValue.getTime() - aValue.getTime();
            }
            
            // Handle numeric values
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return direction === 'asc' ? aValue - bValue : bValue - aValue;
            }
            
            // Handle string comparisons
            const aStr = String(aValue).toLowerCase();
            const bStr = String(bValue).toLowerCase();
            
            if (direction === 'asc') {
                return aStr.localeCompare(bStr);
            } else {
                return bStr.localeCompare(aStr);
            }
        });
        
        return sorted;
    }

    useEffect(() => {
        Promise.all([
            fetchAllSchedules(),
            fetchConfigurations()
        ]).catch(err => {
            console.error("Initial data loading error:", err);
            setError('Failed to load initial data');
            setLoading(false);
        });
    }, []);

    const fetchConfigurations = async () => {
        try {
            const response = await axiosInstance.get('/config/get_configurations/');
            setConfigurations(response.data);
            return response.data;
        } catch (err) {
            console.error("Configuration fetch error:", err);
            setError('Failed to fetch configurations');
            throw err;
        }
    };

    const fetchAllSchedules = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/crud/preceptor/students');
            const data = response.data;
            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('All schedules:', data);
            
            // Extract current curriculums and schedule data
            const currentCurriculums = data.currentCurriculums || [];
            setCurrentCurriculums(currentCurriculums);
            
            // Remove currentCurriculums from data to get only schedule data
            const { currentCurriculums: _, ...scheduleData } = data;
            
            setSchedulesByCurriculum(scheduleData);
            setOriginalScheduleData(scheduleData); // Store original data
            
            const curriculumIds = Object.keys(scheduleData);
            setAvailableCurriculums(curriculumIds);
            if (curriculumIds.length === 0) {
                setSelectedCurriculum('');
            }
            
            // Initialize column visibility for all tables
            const initialVisibility = {};
            curriculumIds.forEach(id => {
                initialVisibility[id] = [...availableColumns]; // Use spread to create a new array
            });
            setVisibleColumnsByTable(initialVisibility);
            
            // Keep default selection as 'current'
            setLoading(false);
            return data;
        } catch (err) {
            console.error("Schedule fetch error:", err);
            setError('Failed to fetch schedules');
            setLoading(false);
            throw err;
        }
    };

    // Add this helper function to format dates
    const formatDateForAPI = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
    };

    // Helper function to parse dates from various formats
    const parseDate = (dateString) => {
        if (!dateString) return null;
        
        // Handle different date formats
        let date;
        if (typeof dateString === 'string') {
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
                const [year, month, day] = dateString.split('-').map(Number);
                date = new Date(year, month - 1, day);
            } else if (dateString.includes('/')) {
                const [day, month, year] = dateString.split('/');
                date = new Date(year, month - 1, day);
            } else {
                date = new Date(dateString);
            }
        } else {
            date = new Date(dateString);
        }
        
        return isNaN(date.getTime()) ? null : date;
    };

    // Helper function to check if two date ranges intersect
    const dateRangesIntersect = (range1Start, range1End, range2Start, range2End) => {
        const start1 = parseDate(range1Start);
        const end1 = parseDate(range1End);
        const start2 = parseDate(range2Start);
        const end2 = parseDate(range2End);
        
        if (!start1 || !end1 || !start2 || !end2) return false;
        
        return start1 <= end2 && start2 <= end1;
    };

    // Frontend date range filtering function
    const filterSchedulesByDateRange = (filterStartDate, filterEndDate, curriculumId) => {
        if (!filterStartDate || !filterEndDate || !originalScheduleData || !curriculumId || !originalScheduleData[curriculumId]) {
            return {};
        }

        const filteredSchedules = {};
        const rotations = originalScheduleData[curriculumId];
        const filteredRotations = rotations.filter(rotationData => {
            let rotationStartDate = null;
            let rotationEndDate = null;

            if (rotationData.dates) {
                rotationStartDate = rotationData.dates.start;
                rotationEndDate = rotationData.dates.end;
            } else if (rotationData.rotationStartDate && rotationData.rotationEndDate) {
                rotationStartDate = rotationData.rotationStartDate;
                rotationEndDate = rotationData.rotationEndDate;
            } else if (rotationData.start_date && rotationData.end_date) {
                rotationStartDate = rotationData.start_date;
                rotationEndDate = rotationData.end_date;
            }

            if (!rotationStartDate || !rotationEndDate) return false;

            return dateRangesIntersect(
                rotationStartDate, rotationEndDate,
                filterStartDate, filterEndDate
            );
        });

        if (filteredRotations.length > 0) {
            filteredSchedules[curriculumId] = filteredRotations;
        }
        
        return filteredSchedules;
    };

    const handleCurriculumChange = (e) => {
        const newCurriculum = e.target.value;
        setSelectedCurriculum(newCurriculum);
        setStartDate(''); // Reset date filters
        setEndDate('');
        // Reset to original data when curriculum is selected
        setSchedulesByCurriculum(originalScheduleData);
    };

    // Helper function to get filtered curriculums based on selection
    const getFilteredCurriculums = () => {
        if (selectedCurriculum && originalScheduleData[selectedCurriculum]) {
            return { [selectedCurriculum]: originalScheduleData[selectedCurriculum] };
        }
        return originalScheduleData;
    };

    const handleStartDateChange = (e) => {
        const newStartDate = e.target.value;
        if (!selectedCurriculum) {
            setStartDate('');
            return;
        }
        setStartDate(newStartDate);
        
        if (newStartDate && endDate) {
            const filteredData = filterSchedulesByDateRange(newStartDate, endDate, selectedCurriculum);
            setSchedulesByCurriculum(filteredData);
            setAvailableCurriculums(Object.keys(filteredData));
        } else if (!newStartDate && !endDate) {
            setSchedulesByCurriculum(getFilteredCurriculums());
            setAvailableCurriculums(Object.keys(getFilteredCurriculums()));
        }
    };

    const handleEndDateChange = (e) => {
        const newEndDate = e.target.value;
        if (!selectedCurriculum) {
            setEndDate('');
            return;
        }
        setEndDate(newEndDate);
        
        if (startDate && newEndDate) {
            const filteredData = filterSchedulesByDateRange(startDate, newEndDate, selectedCurriculum);
            setSchedulesByCurriculum(filteredData);
            setAvailableCurriculums(Object.keys(filteredData));
        } else if (!startDate && !newEndDate) {
            setSchedulesByCurriculum(getFilteredCurriculums());
            setAvailableCurriculums(Object.keys(getFilteredCurriculums()));
        }
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'Date not available';
        
        try {
            const formattedDate = formatMongoDate(dateString);
            return formattedDate !== 'Invalid Date' ? formattedDate : 'Invalid date';
        } catch (err) {
            console.error("Date formatting error:", err, "for date:", dateString);
            return 'Invalid date';
        }
    };

    // Handle export functionality
    const handleExport = (format) => {
        try {
            // Use the current filtered data that's being displayed
            let dataToExport;
            
            if (startDate && endDate) {
                // Use filtered data
                dataToExport = schedulesByCurriculum;
            } else if (selectedCurriculum) {
                // Use specific curriculum
                dataToExport = { [selectedCurriculum]: schedulesByCurriculum[selectedCurriculum] };
            } else {
                dataToExport = originalScheduleData;
            }
                                
            if (Object.keys(dataToExport).length === 0) {
                showError('No schedule data to export.');
                return;
            }

            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Data to export:', dataToExport);

            // Create the actual rendered data structure for export
            const renderedData = [];
            
            // First, collect all visible columns from all displayed tables
            const allVisibleColumns = new Set();
            Object.keys(dataToExport).forEach(curriculumId => {
                const visibleColumns = visibleColumnsByTable[curriculumId] || availableColumns;
                visibleColumns.forEach(col => allVisibleColumns.add(col));
            });
            
            Object.entries(dataToExport).forEach(([curriculumId, curriculumData]) => {
                if (Array.isArray(curriculumData)) {
                    // Apply sorting to the data before processing
                    const sortedData = getSortedData(curriculumData, curriculumId);
                    
                    sortedData.forEach((rotation, rotationIndex) => {
                        // Use the same date logic as the table rendering
                        let dateRange = null;
                        
                        if (rotation.dates) {
                            // Handle the date format from filtered API response
                            dateRange = {
                                startDate: rotation.dates.start,
                                endDate: rotation.dates.end
                            };
                        } else if (rotation.rotationStartDate && rotation.rotationEndDate) {
                            // Handle new MongoDB format
                            dateRange = {
                                startDate: rotation.rotationStartDate,
                                endDate: rotation.rotationEndDate
                            };
                        } else if (rotation.start_date && rotation.end_date) {
                            // Use rotation's own dates (legacy format)
                            dateRange = {
                                startDate: rotation.start_date,
                                endDate: rotation.end_date
                            };
                        }
                        
                        const dateRangeString = dateRange ? 
                            `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}` : 
                            'Date range not available';
                        
                        const actualRotationIndex = rotation.rotation !== undefined ? rotation.rotation : rotationIndex;
                        
                        // Check if rotation has students and if students column is visible anywhere
                        const rotationStudents = getRotationStudents(rotation);

                        if (allVisibleColumns.has('students') && rotationStudents.length > 0) {
                            // Create a row for each student
                            rotationStudents.forEach(student => {
                                const rowData = {};
                                
                                // Always include curriculum and rotation
                                rowData['Curriculum'] = curriculumId;
                                rowData['Rotation'] = `Rotation ${actualRotationIndex + 1}`;
                                
                                // Only include columns that are visible in at least one table
                                if (allVisibleColumns.has('dateRange')) {
                                    rowData['Date Range'] = dateRangeString;
                                }
                                if (allVisibleColumns.has('students')) {
                                    rowData['Name'] = student.name || '';
                                    rowData['Email'] = student.email || '';
                                    rowData['My Role'] = student.isCoprecepted ? 'Co-Preceptor' : 'Preceptor';
                                }
                                
                                renderedData.push(rowData);
                            });
                        } else if (allVisibleColumns.has('students')) {
                            // For rotations with no students, create a single row only if students column is visible
                            const rowData = {};
                            
                            // Always include curriculum and rotation
                            rowData['Curriculum'] = curriculumId;
                            rowData['Rotation'] = `Rotation ${actualRotationIndex + 1}`;
                            
                            // Only include columns that are visible in at least one table
                            if (allVisibleColumns.has('dateRange')) {
                                rowData['Date Range'] = dateRangeString;
                            }
                            if (allVisibleColumns.has('students')) {
                                rowData['Name'] = '';
                                rowData['Email'] = '';
                                rowData['My Role'] = '';
                            }
                            
                            renderedData.push(rowData);
                        }
                    });
                }
            });

            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Final rendered data:', renderedData);

            // Create columns structure for export - based on visible columns across all tables
            // (allVisibleColumns was already calculated above)
            
            // Build dynamic columns structure
            const allColumns = [
                { key: 'Curriculum', label: 'Curriculum' },
                { key: 'Rotation', label: 'Rotation' }
            ];
            
            // Only add columns that are visible in at least one table
            if (allVisibleColumns.has('dateRange')) {
                allColumns.push({ key: 'Date Range', label: 'Date Range' });
            }
            if (allVisibleColumns.has('students')) {
                allColumns.push(
                    { key: 'Name', label: 'Name' },
                    { key: 'Email', label: 'Email' },
                    { key: 'My Role', label: 'My Role' }
                );
            }
            
            const title = 'Preceptor Schedule';
            const filename = `preceptor_schedule_${new Date().toISOString().split('T')[0]}`;
            
            // For PDF, use the original curriculum structure to create separate tables
            // For CSV, use the flattened data structure
            if (format.toUpperCase() === 'PDF') {
                // Prepare table-specific configuration for PDF
                const tableConfigs = {};
                Object.keys(dataToExport).forEach(curriculumId => {
                    const visibleColumns = visibleColumnsByTable[curriculumId] || availableColumns;
                    tableConfigs[curriculumId] = {
                        visibleColumns: visibleColumns,
                        sortConfig: sortState[curriculumId] || { column: null, direction: 'asc' }
                    };
                });
                
                // Pass the original curriculum data structure and table configs for PDF
                exportToPDF(dataToExport, allColumns, title, filename, null, 'curriculum-separated', tableConfigs);
            } else {
                // Use the flattened data for CSV
                exportRenderedTableData(renderedData, allColumns, title, filename, format);
            }
        } catch (error) {
            console.error('Export failed:', error);
            showError('Export failed. Please try again.');
        }
    };

    // Column toggle handler for specific tables
    const handleColumnToggle = (curriculumId, column) => {
        setVisibleColumnsByTable(prev => {
            const currentColumns = prev[curriculumId] || availableColumns;
            const updatedColumns = currentColumns.includes(column)
                ? currentColumns.filter(col => col !== column)
                : [...currentColumns, column];
            
            return {
                ...prev,
                [curriculumId]: updatedColumns
            };
        });
    };

    // Handle table export for specific curriculum
    const handleTableExport = (curriculumId, format = 'CSV') => {
        try {
            const curriculumData = schedulesByCurriculum[curriculumId] || originalScheduleData[curriculumId];
            const visibleColumns = visibleColumnsByTable[curriculumId] || availableColumns;
            
            if (!curriculumData || curriculumData.length === 0) {
                showError('No data available for this table.');
                return;
            }

            // Transform the data to match what's actually rendered in the table
            const renderedData = [];
            
            // Apply sorting to the data before processing
            const sortedData = getSortedData(curriculumData, curriculumId);
            
            sortedData.forEach((rotation, rotationIndex) => {
                // Use the same date logic as the table rendering
                let dateRange = null;
                
                if (rotation.dates) {
                    // Handle the date format from filtered API response
                    dateRange = {
                        startDate: rotation.dates.start,
                        endDate: rotation.dates.end
                    };
                } else if (rotation.rotationStartDate && rotation.rotationEndDate) {
                    // Handle new MongoDB format
                    dateRange = {
                        startDate: rotation.rotationStartDate,
                        endDate: rotation.rotationEndDate
                    };
                } else if (rotation.start_date && rotation.end_date) {
                    // Use rotation's own dates (legacy format)
                    dateRange = {
                        startDate: rotation.start_date,
                        endDate: rotation.end_date
                    };
                }
                
                const dateRangeString = dateRange ? 
                    `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}` : 
                    'Date range not available';
                
                const actualRotationIndex = rotation.rotation !== undefined ? rotation.rotation : rotationIndex;
                
                // Check if rotation has students and if students column is visible
                const rotationStudents = getRotationStudents(rotation);

                if (visibleColumns.includes('students') && rotationStudents.length > 0) {
                    // Create a row for each student
                    rotationStudents.forEach(student => {
                        const rowData = {};
                        
                        // Always include curriculum and rotation
                        rowData['Curriculum'] = curriculumId;
                        rowData['Rotation'] = `Rotation ${actualRotationIndex + 1}`;
                        
                        // Only include visible columns
                        if (visibleColumns.includes('dateRange')) {
                            rowData['Date Range'] = dateRangeString;
                        }
                        if (visibleColumns.includes('students')) {
                            rowData['Name'] = student.name || '';
                            rowData['Email'] = student.email || '';
                            rowData['My Role'] = student.isCoprecepted ? 'Co-Preceptor' : 'Preceptor';
                        }
                        
                        renderedData.push(rowData);
                    });
                } else {
                    // For rotations with no students, only add if students column is visible
                    if (visibleColumns.includes('students')) {
                        const rowData = {};
                        
                        // Always include curriculum and rotation
                        rowData['Curriculum'] = curriculumId;
                        rowData['Rotation'] = `Rotation ${actualRotationIndex + 1}`;
                        
                        // Only include visible columns
                        if (visibleColumns.includes('dateRange')) {
                            rowData['Date Range'] = dateRangeString;
                        }
                        if (visibleColumns.includes('students')) {
                            rowData['Name'] = '';
                            rowData['Email'] = '';
                            rowData['My Role'] = '';
                        }
                        
                        renderedData.push(rowData);
                    }
                }
            });

            // Create columns structure for export based on visible columns
            const exportColumns = [
                { key: 'Curriculum', label: 'Curriculum' },
                { key: 'Rotation', label: 'Rotation' }
            ];
            
            // Only add columns that are visible
            if (visibleColumns.includes('dateRange')) {
                exportColumns.push({ key: 'Date Range', label: 'Date Range' });
            }
            if (visibleColumns.includes('students')) {
                exportColumns.push(
                    { key: 'Name', label: 'Name' },
                    { key: 'Email', label: 'Email' },
                    { key: 'My Role', label: 'My Role' }
                );
            }

            const title = `Preceptor Schedule - ${getCurriculumDisplayName(curriculumId)}`;
            const filename = `preceptor_schedule_${curriculumId}_${new Date().toISOString().split('T')[0]}`;
            
            if (import.meta.env.VITE_DEBUG_MODE === 'true') {
                console.log('Table export data:', renderedData);
                console.log('Table export columns:', exportColumns);
            }
            
            exportRenderedTableData(renderedData, exportColumns, title, filename, format);
        } catch (error) {
            console.error('Table export failed:', error);
            showError('Export failed. Please try again.');
        }
    };

    // Clear all filters
    const clearAllFilters = () => {
        setSelectedCurriculum('');
        setStartDate('');
        setEndDate('');
        setSchedulesByCurriculum(originalScheduleData);
        setAvailableCurriculums(Object.keys(originalScheduleData));
    };

    // Add helper function to get curriculum display name
    const getCurriculumDisplayName = (curriculumId) => {
        return curriculumId || 'Unknown Curriculum';
    };

    if (loading) {
        return (
            <div style={{ width: '100%', padding: 0, margin: 0 }}>
                <LoadingSpinner text="Loading schedules..." variant="page" />
            </div>
        );
    }

    const getRotationDateRange = (curriculumId, rotationIndex) => {
        // For curriculum-based data, we'll need to extract dates from the rotation data itself
        // This might need adjustment based on the actual API response structure
        return null; // Placeholder - will use rotation's own dates
    };

    const renderCurriculumSection = (curriculumId, rotations) => {
        const visibleColumns = visibleColumnsByTable[curriculumId] || availableColumns;
        
        return (
            <div key={curriculumId} className="modern-schedule-table-container">
                <div className="modern-table-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3>Curriculum: {getCurriculumDisplayName(curriculumId)}</h3>
                    <ColumnSelector 
                        availableColumns={availableColumns}
                        visibleColumns={visibleColumns}
                        onColumnToggle={(column) => handleColumnToggle(curriculumId, column)}
                        onExportTable={() => handleTableExport(curriculumId)}
                    />
                </div>
                <table className="modern-schedule-table">
                    <thead>
                        <tr key={`header-${visibleColumns.join('-')}`} style={{
                            background: 'linear-gradient(90deg, #2b4663 0%, #3c5a7a 25%, #4a90e2 75%, #5ba3f5 100%) !important',
                            backgroundSize: '100% 100% !important',
                            boxShadow: '0 8px 32px rgba(43, 70, 99, 0.25)',
                            position: 'relative',
                            width: '100%',
                            display: 'table-row'
                        }}>
                            {visibleColumns.includes('rotation') && (
                                <SortableTableHeader 
                                    label="Rotation" 
                                    columnKey="rotation" 
                                    sortState={sortState[curriculumId] || { column: '', direction: 'asc' }} 
                                    onSort={(col) => handleSort(curriculumId, col)}
                                    style={{
                                        background: 'none !important',
                                        backgroundColor: 'transparent !important',
                                        color: '#fff !important',
                                        fontWeight: '700',
                                        padding: '1rem 1rem',
                                        textAlign: 'left',
                                        border: 'none',
                                        borderRight: 'none',
                                        fontSize: '0.95rem',
                                        letterSpacing: '0.75px',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                                        position: 'relative',
                                        textTransform: 'uppercase',
                                        fontFamily: 'system-ui, -apple-system, sans-serif',
                                        transition: 'none !important'
                                    }}
                                />
                            )}
                            {visibleColumns.includes('dateRange') && (
                                <SortableTableHeader 
                                    label="Date Range" 
                                    columnKey="dateRange" 
                                    sortState={sortState[curriculumId] || { column: '', direction: 'asc' }} 
                                    onSort={(col) => handleSort(curriculumId, col)}
                                    style={{
                                        background: 'none !important',
                                        backgroundColor: 'transparent !important',
                                        color: '#fff !important',
                                        fontWeight: '700',
                                        padding: '1rem 1rem',
                                        textAlign: 'left',
                                        border: 'none',
                                        borderRight: 'none',
                                        fontSize: '0.95rem',
                                        letterSpacing: '0.75px',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                                        position: 'relative',
                                        textTransform: 'uppercase',
                                        fontFamily: 'system-ui, -apple-system, sans-serif',
                                        transition: 'none !important'
                                    }}
                                />
                            )}
                            {visibleColumns.includes('students') && (
                                <SortableTableHeader 
                                    label="Students" 
                                    columnKey="students" 
                                    sortState={sortState[curriculumId] || { column: '', direction: 'asc' }} 
                                    onSort={(col) => handleSort(curriculumId, col)}
                                    style={{
                                        background: 'none !important',
                                        backgroundColor: 'transparent !important',
                                        color: '#fff !important',
                                        fontWeight: '700',
                                        padding: '1rem 1rem',
                                        textAlign: 'left',
                                        border: 'none',
                                        borderRight: 'none',
                                        fontSize: '0.95rem',
                                        letterSpacing: '0.75px',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                                        position: 'relative',
                                        textTransform: 'uppercase',
                                        fontFamily: 'system-ui, -apple-system, sans-serif',
                                        transition: 'none !important'
                                    }}
                                />
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {getSortedData(rotations, curriculumId).map((rotationData, index) => {
                            // Get rotation dates based on the API response structure
                            let dateRange = null;
                            
                            if (rotationData.dates) {
                                // Handle the date format from filtered API response
                                dateRange = {
                                    startDate: rotationData.dates.start,
                                    endDate: rotationData.dates.end
                                };
                            } else if (rotationData.rotationStartDate && rotationData.rotationEndDate) {
                                // Handle new MongoDB format
                                dateRange = {
                                    startDate: rotationData.rotationStartDate,
                                    endDate: rotationData.rotationEndDate
                                };
                            } else if (rotationData.start_date && rotationData.end_date) {
                                // Use rotation's own dates (legacy format)
                                dateRange = {
                                    startDate: rotationData.start_date,
                                    endDate: rotationData.end_date
                                };
                            }
                            
                            const rotationIndex = rotationData.rotation !== undefined ? rotationData.rotation : index;
                            
                            return (
                                <tr key={index} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                                    {visibleColumns.includes('rotation') && (
                                        <td>Rotation {rotationIndex + 1}</td>
                                    )}
                                    {visibleColumns.includes('dateRange') && (
                                        <td>
                                            {dateRange ? 
                                                `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}` : 
                                                'Date range not available'}
                                        </td>
                                    )}
                                    {visibleColumns.includes('students') && (
                                        <td>
                                            {getRotationStudents(rotationData).length > 0 ? (
                                                <div className="students-table">
                                                    <table>
                                                        <thead>
                                                            <tr>
                                                                <th>Name</th>
                                                                <th>Email</th>
                                                                <th>My Role</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {getRotationStudents(rotationData).map((student, sIndex) => (
                                                                <tr key={sIndex}>
                                                                    <td>{student.name}</td>
                                                                    <td>{student.email}</td>
                                                                    <td>
                                                                        <span className={student.isCoprecepted ? "coprecepted-badge" : "preceptor-badge"}>
                                                                            {student.isCoprecepted ? 'Co-Preceptor' : 'Preceptor'}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : (
                                                <OffBadge />
                                            )}
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    const totalCurriculumsLoaded = Object.keys(originalScheduleData).length;
    const visibleCurriculums = Object.keys(schedulesByCurriculum).length;
    const visibleRotations = Object.values(schedulesByCurriculum).reduce(
        (sum, rotations) => sum + (Array.isArray(rotations) ? rotations.length : 0),
        0
    );
    const activeFilters = Number(Boolean(selectedCurriculum)) + Number(Boolean(startDate || endDate));
    const hasAnyFilters = Boolean(selectedCurriculum || startDate || endDate);

    return (
        <div className="view-admin-schedules-page vs-shell" style={{ width: '100%', minHeight: '100vh', boxSizing: 'border-box', padding: '2rem', background: '#ffffff', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
            <div className="view-admin-schedules-header">
                <div className="view-admin-schedules-header-inner">
                    <div className="view-admin-schedules-header-left">
                        <div className="view-admin-schedules-header-icon">
                            <span aria-hidden>🗓️</span>
                        </div>
                        <div>
                            <h1 className="view-admin-schedules-title">My Schedules</h1>
                            <p className="view-admin-schedules-subtitle">View and manage your schedules across all curricula</p>
                        </div>
                    </div>
                    <div className="view-admin-schedules-header-actions">
                        <ExportButton
                            onExport={handleExport}
                            formats={["PDF", "CSV"]}
                            disabled={loading || Object.keys(schedulesByCurriculum).length === 0}
                            customLabel="Export Showing Tables"
                        />
                    </div>
                </div>
            </div>

            <div className="vs-kpi-grid">
                <div className="vs-kpi-card">
                    <p className="vs-kpi-value">{totalCurriculumsLoaded}</p>
                    <p className="vs-kpi-label">Available curriculums</p>
                </div>
                <div className="vs-kpi-card">
                    <p className="vs-kpi-value">{visibleCurriculums}</p>
                    <p className="vs-kpi-label">Visible curriculums</p>
                </div>
                <div className="vs-kpi-card">
                    <p className="vs-kpi-value">{visibleRotations}</p>
                    <p className="vs-kpi-label">Visible rotations</p>
                </div>
                <div className="vs-kpi-card">
                    <p className="vs-kpi-value">{activeFilters}</p>
                    <p className="vs-kpi-label">Active filters</p>
                </div>
            </div>
            
            {error && (
                <div className="notification error-notification" role="alert">
                    <span className="notification-icon">⚠️</span>
                    <div className="notification-content">{error}</div>
                </div>
            )}
            
            <div className="modern-schedule-controls">
                <div className="control-group control-group--curriculum">
                    <label className="control-label">📚 Curriculum</label>
                    <select
                        value={selectedCurriculum}
                        onChange={handleCurriculumChange}
                        className="modern-select"
                        disabled={Object.keys(originalScheduleData).length === 0}
                    >
                        {Object.keys(originalScheduleData).length === 0 ? (
                            <option value="">No curriculums found</option>
                        ) : (
                            <>
                        <option value="">Select curriculum</option>
                        {Object.keys(originalScheduleData).map(curriculumId => (
                            <option key={curriculumId} value={curriculumId}>
                                {getCurriculumDisplayName(curriculumId)}
                            </option>
                        ))}
                            </>
                        )}
                    </select>
                </div>

                <div className="control-group control-group--date-range">
                    <label className="control-label">📅 Date Range</label>
                    <div className="date-range-inner">
                        <div className="date-range-inputs">
                            <input
                                type="date"
                                value={startDate}
                                onChange={handleStartDateChange}
                                className="modern-input"
                                aria-label="Start date"
                                disabled={!selectedCurriculum}
                            />
                            <span className="date-range-arrow">→</span>
                            <input
                                type="date"
                                value={endDate}
                                onChange={handleEndDateChange}
                                min={startDate}
                                className="modern-input"
                                aria-label="End date"
                                disabled={!selectedCurriculum}
                            />
                        </div>
                    </div>
                </div>

                <div className="control-group control-group--actions">
                    <button
                        type="button"
                        onClick={clearAllFilters}
                        className="modern-button modern-button--clear-filters"
                        disabled={!hasAnyFilters}
                    >
                        🗑️ Clear all filters
                    </button>
                </div>
            </div>

                <div className="schedule-results">
                    <div className="list-section">
                    {startDate && endDate ? (
                        // Show filtered results across all curriculums that match the date range
                        Object.keys(schedulesByCurriculum).length > 0 ? (
                            <div>
                                <div style={{ 
                                    marginBottom: '1.5rem', 
                                    padding: '1rem', 
                                    background: '#f8f9fa', 
                                    borderRadius: '8px', 
                                    color: '#495057',
                                    textAlign: 'center',
                                    border: '1px solid #e9ecef'
                                }}>
                                    <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700' }}>
                                        📅 Filtered Results for Date Range: {formatDate(startDate)} - {formatDate(endDate)}
                                    </h3>
                                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', opacity: 0.9 }}>
                                        Showing {Object.keys(schedulesByCurriculum).length} curriculum(s) with matching rotations
                                    </p>
                                </div>
                                {Object.keys(schedulesByCurriculum)
                                    .sort() // Sort curriculum IDs for consistent ordering
                                    .map(curriculumId => (
                                        <div key={curriculumId} style={{ marginBottom: '2rem' }}>
                                            {renderCurriculumSection(curriculumId, schedulesByCurriculum[curriculumId])}
                                        </div>
                                    ))}
                            </div>
                        ) : (
                            <div className="no-schedule-message">
                                <div style={{ 
                                    padding: '2rem', 
                                    textAlign: 'center', 
                                    background: '#fef2f2', 
                                    border: '2px dashed #fca5a5', 
                                    borderRadius: '12px',
                                    color: '#991b1b'
                                }}>
                                    <span style={{ fontSize: '3rem', marginBottom: '1rem', display: 'block' }}>📅❌</span>
                                    <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.3rem', fontWeight: '700' }}>
                                        No Schedules Found
                                    </h3>
                                    <p style={{ margin: '0 0 1rem 0', fontSize: '1rem' }}>
                                        No schedules found for the selected date range:
                                    </p>
                                    <p style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', fontWeight: '600' }}>
                                        {formatDate(startDate)} - {formatDate(endDate)}
                                    </p>
                                    <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.8 }}>
                                        Try adjusting your date range or clear the filters to see all schedules.
                                    </p>
                                </div>
                            </div>
                        )
                    ) : (() => {
                        // Determine what to show based on curriculum selection
                        const dataToShow = selectedCurriculum && schedulesByCurriculum[selectedCurriculum] 
                                ? { [selectedCurriculum]: schedulesByCurriculum[selectedCurriculum] }
                                : schedulesByCurriculum;
                        
                        if (Object.keys(dataToShow).length > 0) {
                            let headerText = '';
                            let subText = '';
                            
                            if (selectedCurriculum && selectedCurriculum !== '') {
                                headerText = `🎯 Curriculum Filter: ${getCurriculumDisplayName(selectedCurriculum)}`;
                                subText = 'Showing selected curriculum';
                            } else {
                                headerText = '📚 All Curriculums - Complete Schedule Overview';
                                subText = `Showing all ${Object.keys(dataToShow).length} curriculum(s)`;
                            }
                            
                            return (
                                <div>
                                    <div style={{ 
                                        marginBottom: '1.5rem', 
                                        padding: '1rem', 
                                        background: '#f8f9fa', 
                                        borderRadius: '8px', 
                                        color: '#495057',
                                        textAlign: 'center',
                                        border: '1px solid #e9ecef'
                                    }}>
                                        <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700' }}>
                                            {headerText}
                                        </h3>
                                        <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', opacity: 0.9 }}>
                                            {subText}
                                        </p>
                                    </div>
                                    {Object.keys(dataToShow)
                                        .sort() // Sort curriculum IDs for consistent ordering
                                        .map(curriculumId => (
                                            <div key={curriculumId} style={{ marginBottom: '2rem' }}>
                                                {renderCurriculumSection(curriculumId, dataToShow[curriculumId])}
                                            </div>
                                        ))}
                                </div>
                            );
                        } else {
                            return (
                                <div className="no-schedule-message">
                                    <div style={{ 
                                        padding: '2rem', 
                                        textAlign: 'center', 
                                        background: '#f8fafc', 
                                        border: '2px dashed #cbd5e1', 
                                        borderRadius: '12px',
                                        color: '#475569'
                                    }}>
                                        <span style={{ fontSize: '3rem', marginBottom: '1rem', display: 'block' }}>📋</span>
                                        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.3rem', fontWeight: '700' }}>
                                            No Schedule Data Available
                                        </h3>
                                        <p style={{ margin: 0, fontSize: '1rem' }}>
                                            No schedule data is currently available. Please refresh the page or contact support.
                                        </p>
                                    </div>
                                </div>
                            );
                        }
                    })()}
                    </div>
                </div>
        </div>
    );
};

export default ViewPreceptorSchedule;
